import { call, put, takeEvery } from 'redux-saga/effects';
import { message } from 'antd';

import i18n from '../../i18n';
import doCreatePEPHealthService from '@redux/doCreatePEPHealthService';
import { IntegrationOrgConfig, PEPHealthService } from '@liquid-state/pephealth-client';
import {
  ICreateOrgConfig,
  ILoadOrgConfig,
  loadOrgConfigSuccess,
  loadOrgConfigNotConfigured,
  loadOrgConfigFailure,
  IEditOrgConfig,
  createOrgConfigSuccess,
  createOrgConfigFailure,
  editOrgConfigSuccess,
  editOrgConfigFailure,
} from './actions';
import { DEFAULT_API_KEY_MASKING_VALUE } from '../constants';

export default function*() {
  yield takeEvery<any>('integrations/load-org-config', doLoadOrgConfig);
  yield takeEvery<any>('integrations/create-org-config', doCreateOrgConfig);
  yield takeEvery<any>('integrations/edit-org-config', doEditOrgConfig);
}

function maskAPIKeyInOrgConfigData(orgConfigData: { api_key?: string }) {
  if (orgConfigData && orgConfigData['api_key']) {
    return { ...orgConfigData, api_key: DEFAULT_API_KEY_MASKING_VALUE };
  }
  return orgConfigData;
}

function prepareOrgConfigForState(orgConfigData: { api_key?: string }, organisationId?: string) {
  if (!organisationId) return maskAPIKeyInOrgConfigData(orgConfigData);

  const result = { ...orgConfigData };
  delete result.api_key;
  return result;
}

function cleanOrgConfigUpdateData(orgConfigData: any) {
  const result = { ...orgConfigData };
  if (
    orgConfigData &&
    (orgConfigData['api_key'] === DEFAULT_API_KEY_MASKING_VALUE ||
      (typeof orgConfigData['api_key'] === 'string' && !orgConfigData['api_key'].trim()))
  ) {
    delete result['api_key'];
  }
  return result;
}

function* errorDetails(error: any): any {
  const response = error?.response;
  let code = response?.data?.error_code || response?.data?.code || error?.code;

  if (!code && response && typeof response.json === 'function') {
    try {
      const data = yield call([response, response.json]);
      code = data?.error_code || data?.code;
    } catch (_error) {
      // A response without a readable error body still carries a useful HTTP status.
    }
  }

  return {
    status: response?.status,
    code,
  };
}

const feedbackKey = (operation: 'loading' | 'create' | 'edit', status?: number) => {
  const statusKey = {
    403: 'Forbidden',
    404: 'TargetNotFound',
    502: 'Upstream',
  }[status || 0];

  return `integrations:organisationConfig.${operation}${statusKey || 'Error'}`;
};

function* doLoadOrgConfig({ payload: { integrationSlug, organisationId } }: ILoadOrgConfig): any {
  try {
    const service: PEPHealthService = yield call(doCreatePEPHealthService);
    const result: IntegrationOrgConfig = yield call(
      service.getIntegrationOrgConfig,
      integrationSlug,
      organisationId,
    );
    if (result && result.organisationId) {
      const finalResult = {
        ...result,
        data: prepareOrgConfigForState(result.data, organisationId),
      };
      yield put(loadOrgConfigSuccess(integrationSlug, finalResult, organisationId));
    }
  } catch (e) {
    const err: any = e;
    const { status, code } = yield call(errorDetails, err);
    if (status === 404 && code !== 'ORGANISATION_NOT_FOUND') {
      yield put(loadOrgConfigNotConfigured(integrationSlug, organisationId));
      return;
    }
    yield put(loadOrgConfigFailure(integrationSlug, organisationId, status, code));
    yield call(message.error, i18n.t(feedbackKey('loading', status)));
  }
}

function* doCreateOrgConfig({
  payload: { integrationSlug, configData, organisationId },
}: ICreateOrgConfig): any {
  try {
    const service: PEPHealthService = yield call(doCreatePEPHealthService);

    const data = cleanOrgConfigUpdateData(configData);
    const result: IntegrationOrgConfig = yield call(
      service.createIntegrationOrgConfig,
      integrationSlug,
      data,
      organisationId,
    );
    if (result) {
      const finalResult = {
        ...result,
        data: prepareOrgConfigForState(result.data, organisationId),
      };
      yield put(createOrgConfigSuccess(integrationSlug, finalResult, organisationId));
      yield call(message.success, i18n.t('integrations:organisationConfig.createSuccess'));
    }
  } catch (e) {
    const err: any = e;
    const { status, code } = yield call(errorDetails, err);
    yield put(createOrgConfigFailure(integrationSlug, organisationId, status, code));
    yield call(message.error, i18n.t(feedbackKey('create', status)));
  }
}

function* doEditOrgConfig({
  payload: { integrationSlug, isEnabled, configData, organisationId },
}: IEditOrgConfig): any {
  try {
    const service: PEPHealthService = yield call(doCreatePEPHealthService);

    const data = cleanOrgConfigUpdateData(configData);
    const result: IntegrationOrgConfig = yield call(
      service.updateIntegrationOrgConfig,
      integrationSlug,
      isEnabled,
      data,
      organisationId,
    );
    if (result) {
      const finalResult = {
        ...result,
        data: prepareOrgConfigForState(result.data, organisationId),
      };
      yield put(editOrgConfigSuccess(integrationSlug, finalResult, organisationId));
      yield call(message.success, i18n.t('integrations:organisationConfig.editSuccess'));
    }
  } catch (e) {
    const err: any = e;
    const { status, code } = yield call(errorDetails, err);
    yield put(editOrgConfigFailure(integrationSlug, organisationId, status, code));
    yield call(message.error, i18n.t(feedbackKey('edit', status)));
  }
}
